import { t } from "adapters/i18n";
import type { Dispatch } from "react-redux";
import herdService from "services/herdService";
import { getErrorMessage } from "shared/errorUtils";
import {
  type MovementsDispatchAction,
  exibirCarregando,
  listFarmEnrichedHerdRecords,
  listOperationTypes,
  notifyError,
  notifySuccess,
} from "../actions";
import type { RootState } from "../reducers";

export const fetchOperationTypes =
  () =>
  async (dispatch: Dispatch<MovementsDispatchAction>): Promise<void> => {
    try {
      const operationTypes = await herdService.listOperationTypes();

      dispatch(listOperationTypes(operationTypes.data));
    } catch (error) {
      const message = getErrorMessage(
        error,
        t("movements:fetch-operation-types-error")
      );

      dispatch(notifyError({ mensagem: message }));
    }
  };

export const fetchFarmEnrichedHerdRecords =
  () =>
  async (
    dispatch: Dispatch<MovementsDispatchAction>,
    getState: () => RootState
  ): Promise<void> => {
    try {
      dispatch(exibirCarregando(true));
      const { fazenda } = getState();

      if (fazenda.FazendaSelecionada == null) {
        throw new Error(t("common:no-farm-selected"));
      }

      const enrichedRecords = await herdService.fetchEnrichedFarmMovements(
        fazenda.FazendaSelecionada
      );

      dispatch(listFarmEnrichedHerdRecords(enrichedRecords.data));
    } catch (error) {
      const message = getErrorMessage(
        error,
        t("movements:fetch-herd-records-error")
      );

      dispatch(notifyError({ mensagem: message }));
    } finally {
      dispatch(exibirCarregando(false));
    }
  };

export const createHerdRecord =
  (payload: Omit<Parameters<typeof herdService.createRecord>[0], "farmId">) =>
  async (
    dispatch: Dispatch<MovementsDispatchAction>,
    getState: () => RootState
  ): Promise<void> => {
    try {
      const { fazenda } = getState();

      if (fazenda.FazendaSelecionada == null) {
        throw new Error(t("common:no-farm-selected"));
      }

      await herdService.createRecord({
        ...payload,
        farmId: fazenda.FazendaSelecionada,
      });

      dispatch(
        notifySuccess({ mensagem: t("movements:create-herd-record-success") })
      );
    } catch (error) {
      const message = getErrorMessage(
        error,
        t("movements:create-herd-record-error")
      );

      dispatch(notifyError({ mensagem: message }));
    }
  };

export const deleteHerdRecord =
  (recordId: number) =>
  async (dispatch: Dispatch<MovementsDispatchAction>): Promise<void> => {
    try {
      await herdService.deleteRecord(recordId);

      dispatch(
        notifySuccess({ mensagem: t("movements:delete-herd-record-success") })
      );
    } catch (error) {
      const message = getErrorMessage(
        error,
        t("movements:delete-herd-record-error")
      );

      dispatch(notifyError({ mensagem: message }));
    }
  };

export const updateHerdRecord =
  (
    recordId: number,
    payload: Partial<Parameters<typeof herdService.updateRecord>[1]>
  ) =>
  async (dispatch: Dispatch<MovementsDispatchAction>): Promise<void> => {
    try {
      await herdService.updateRecord(recordId, payload);

      dispatch(
        notifySuccess({ mensagem: t("movements:update-herd-record-success") })
      );
    } catch (error) {
      const message = getErrorMessage(
        error,
        t("movements:update-herd-record-error")
      );

      dispatch(notifyError({ mensagem: message }));
    }
  };
